import {useEffect,useState} from 'react';
import {getServerTime,type AwardStreamBoundary} from '@platform/client';
import type {Snapshot} from '@platform/contracts/x';
import {canBuy,canBuyFromRoom} from './roomFlow';

export type CardDraft={drawId:string;drawNumber?:string|null;catalogHash:string;ids:string[];amountText:string;generation:number;nextDrawId?:string};

// This is one unsubmitted, memory-only selection. Financial requests have their
// own immutable identity and persistence in usePurchase; never expire them here.
function resolve(draft:CardDraft|null,current:Snapshot|null,previous:Snapshot|null,stream:AwardStreamBoundary):CardDraft|null{
  if(!draft||!current)return draft;
  if(!draft.nextDrawId){
    if(!stream.ready)return draft;
    if(current.drawId===draft.drawId){
      draft={...draft,generation:stream.generation,nextDrawId:current.upcoming?.drawId};
      if(!draft.nextDrawId)return draft;
    }else{
      // With pre-sales disabled the successor may first appear as current.
      // After a visibility/reconnect gap an unknown successor cannot be guessed.
      if(stream.generation!==draft.generation||previous?.drawId!==draft.drawId)return null;
      draft={...draft,nextDrawId:current.drawId};
    }
  }
  const target=[current,current.upcoming].find(draw=>draw?.drawId===draft.nextDrawId);
  if(!target||!canBuy(target))return null;
  if(target.salesCloseAtUtc&&!(Date.parse(target.salesCloseAtUtc)>getServerTime(current)))return null;
  return draft;
}

export function useClosedCardDraft(current:Snapshot|null,previous:Snapshot|null,stream:AwardStreamBoundary){
  const [stored,setDraft]=useState<CardDraft|null>(null);
  const draft=resolve(stored,current,previous,stream);
  const target=draft?.nextDrawId?[current,current?.upcoming].find(draw=>draw?.drawId===draft.nextDrawId):undefined;
  useEffect(()=>{
    if(!stored||!current)return;
    const refresh=()=>{
      const next=resolve(stored,current,previous,stream);
      setDraft(value=>value!==stored?value:!next?null:
        next.nextDrawId===stored.nextDrawId&&next.generation===stored.generation?stored:next);
    };
    refresh();
    // One deadline timer using the shared server clock, without extra polling.
    // Visibility/focus also rechecks after browser timer suspension.
    const remaining=target?.salesCloseAtUtc?Date.parse(target.salesCloseAtUtc)-getServerTime(current):null;
    const timer=remaining!==null&&Number.isFinite(remaining)?setTimeout(refresh,Math.max(0,Math.min(remaining+1,2147483647))):undefined;
    document.addEventListener('visibilitychange',refresh);window.addEventListener('focus',refresh);
    return()=>{clearTimeout(timer);document.removeEventListener('visibilitychange',refresh);window.removeEventListener('focus',refresh);};
  },[stored,current,previous,stream]);
  const catalogChanged=!!(draft&&target&&draft.catalogHash!==target.x.catalogHash);
  const resumeTarget=!catalogChanged&&stream.ready&&current&&target&&canBuyFromRoom(target,current)?target:undefined;
  // Recheck at the click as well: a suspended timer cannot extend the deadline.
  const canResume=()=>!!(current&&resumeTarget&&resolve(stored,current,previous,stream));
  return {draft,setDraft,resumeTarget,canResume,catalogChanged};
}
